import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Yii } from 'libraries/interfaces/Yii';
import { Subscription, forkJoin } from 'rxjs';
import { AirSearchHeaderActionService } from '../air-search-header/air-search-header-action/air-search-header-action.service';
import { airSearchFormDefaultValues } from './air-search-form-default-values';
import { airSearchFormMultipleDefaultValues } from './air-search-form-multiple-default-values';
import { EWayType, IAirSearchSegment } from "./air-search-segment-oneway-return/air-search-segment-oneway-return.component";
import { IconDefinition, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { EAirSearchPermissions } from '../e-air-search-permissions';
import { EAirSearchContext, IAirSearchRequest } from "../../air-search/air-search.component";

class Wording {
    public readonly airSearchAction: string = Yii.t('wui-iqba-js', 'Rechercher');
    public readonly moreCriteria: string = Yii.t('wui-iqba-js', 'Critères supplémentaires');
}

@Component({
    selector: 'air-search-form',
    templateUrl: './air-search-form.component.html',
    styleUrls: ['./air-search-form.component.scss']
})
export class AirSearchFormComponent implements OnInit, OnDestroy {
    @Input()
    public wayType: EWayType;
    @Input()
    public airSearchFormValues: IAirSearchRequest = null;
    @Input()
    public airSearchContext: EAirSearchContext = null;
    @Output()
    public readonly onSearch: EventEmitter<IAirSearchRequest> = new EventEmitter<IAirSearchRequest>();
    public readonly wording: Wording = new Wording();
    public eWayType: typeof EWayType = EWayType;
    public eAirSearchContext: typeof EAirSearchContext = EAirSearchContext;
    public airSearchForm: FormGroup;
    public airSearchFormChangesSubscription: Subscription;
    public isMoreCriteria: boolean = false;
    public isMoreCriteriaToggle: boolean = false;
    public iconDown: IconDefinition = faChevronDown;
    public iconUp: IconDefinition = faChevronUp;
    public eAirSearchPermissions: typeof EAirSearchPermissions = EAirSearchPermissions;
    public multipleSegmentLength: number;
    public segmentValues: IAirSearchSegment[];
    public showFlexibilityForm: boolean = true;
    private airSearchHeaderActionSubscription: Subscription = new Subscription();
    private onPatchValueTimeout: number;

    constructor(
        private readonly airSearchHeaderActionService: AirSearchHeaderActionService,
        private readonly permissionResourceService: PermissionResourceService,
    ) {
    }

    public ngOnInit(): void {
        this.initMultipleSegmentLength();
        this.initAirSearchForm();
        this.initIsMoreCriteria();
        this.initAirSearchFormChangesSubscription();
        this.initAirSearchHeaderActionSubscription();
    }

    public ngOnDestroy(): void {
        if (this.onPatchValueTimeout) {
            clearTimeout(this.onPatchValueTimeout);
        }
        if(this.airSearchFormChangesSubscription){
            this.airSearchFormChangesSubscription.unsubscribe();
        }
        if(this.airSearchHeaderActionSubscription){
            this.airSearchHeaderActionSubscription.unsubscribe();
        }
    }

    public onSubmit(): void {
        if (this.airSearchForm.valid) {
            this.onSearch.emit(this.airSearchForm.value);
        }
    }

    private initMultipleSegmentLength(): void {
        this.multipleSegmentLength = this.airSearchFormValues?.segment?.length ?? 2;
    }

    private initAirSearchForm(): void {
        this.airSearchForm = new FormGroup({});
        this.onPatchValueTimeout = setTimeout(() => {
            this.airSearchForm.patchValue(this.airSearchFormValues, {emitEvent: false});
        });
    }

    private initIsMoreCriteria(): void {
        forkJoin([
            this.permissionResourceService.hasPermission(this.eAirSearchPermissions.IQBA_SEARCHFORM_ADDITIONAL_PRICE),
            this.permissionResourceService.hasPermission(this.eAirSearchPermissions.IQBA_SEARCHFORM_ADDITIONAL_CARRIER),
        ]).subscribe({
            next: ([hasPermissionPrice, hasPermissionCarrier]: [boolean, boolean]): void => {
                this.isMoreCriteria = hasPermissionPrice || hasPermissionCarrier;
            }
        });
    }

    private initAirSearchFormChangesSubscription(): void {
        this.airSearchFormChangesSubscription = this.airSearchForm.valueChanges.subscribe((airSearchFormValues: IAirSearchRequest) => {
            this.showFlexibilityForm = airSearchFormValues.segment?.length === 2;
           // uncomment nextLine for debug;
           // console.log(airSearchFormValues);
        });
    }

    private initAirSearchHeaderActionSubscription(): void {
        this.airSearchHeaderActionSubscription.add(
            this.airSearchHeaderActionService.wayTypeChangeObservable.subscribe((wayType: EWayType) => {
                this.wayType = wayType;
            }));
        this.airSearchHeaderActionSubscription.add(
            this.airSearchHeaderActionService.resetAirSearchFormObservable.subscribe(() => {
                this.airSearchForm.reset(this.wayType === this.eWayType.Multiple ? airSearchFormMultipleDefaultValues : airSearchFormDefaultValues);
            }));
    }
}
